import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import {
  getMarineVesselFieldRole,
  MarineVesselFieldKeys,
  MarineVesselFieldRole,
} from './marine-vessel-property-input/hin-decode';
import { VehicleFieldKeys } from './vehicle-property-input/vin-decode';

/**
 * Decides where VIN and HIN decoding happens for an asset type, so the asset form and the asset type
 * editor's status read off the same rules.
 *
 * - Grouped: an object property with a VIN (or HIN) child gets the Road Vehicle (or Marine Vessel) form,
 *   which decodes into its own children.
 * - Ungrouped: top-level fields decode when the VIN (or HIN) sits alongside at least one other detail field.
 */

export type DecodeKind = 'VIN' | 'HIN';

export const ROAD_VEHICLE_FORM_ID = 'road_vehicle';
export const MARINE_VESSEL_FORM_ID = 'marine_vessel';

type Labels = Array<string | null | undefined>;

interface DecodeField {
  labels: Labels;
  valueKey: string;
  name: string;
}

export interface DecodeRole<R extends string> {
  role: R;
  label: string;
  /** Plain-language summary of the names the role matches, for admin help text. */
  accepts: string;
}

export interface DecodeDefinition<R extends string> {
  kind: DecodeKind;
  idRole: R;
  formId: string;
  roles: DecodeRole<R>[];
  roleOf: (labels: Labels) => R | null;
}

export const VIN_DECODE: DecodeDefinition<PropertySet.VehicleFieldRole> = {
  kind: 'VIN',
  idRole: 'vin',
  formId: ROAD_VEHICLE_FORM_ID,
  roleOf: PropertySet.getVehicleFieldRole,
  roles: [
    { role: 'vin', label: 'VIN', accepts: '"VIN", or a key ending in "vin" (e.g. roadVehicleVIN)' },
    { role: 'make', label: 'Make', accepts: '"Make", anything ending in "make", or containing the word "make"' },
    { role: 'model', label: 'Model', accepts: '"Model", or anything ending in "model"' },
    { role: 'year', label: 'Year', accepts: '"Year", "Model Year", or anything ending in "year"' },
    { role: 'trim', label: 'Trim', accepts: '"Trim", or anything ending in "trim"' },
  ],
};

export const HIN_DECODE: DecodeDefinition<MarineVesselFieldRole> = {
  kind: 'HIN',
  idRole: 'hin',
  formId: MARINE_VESSEL_FORM_ID,
  roleOf: getMarineVesselFieldRole,
  roles: [
    { role: 'hin', label: 'HIN', accepts: '"HIN", or a key ending in "hin" (e.g. marineVesselHIN)' },
    { role: 'manufacturer', label: 'Manufacturer', accepts: '"Manufacturer", or anything ending in "manufacturer"' },
    { role: 'serial', label: 'Serial number', accepts: '"Serial", "Serial Number", or anything ending in "serial"' },
    { role: 'buildMonth', label: 'Build month', accepts: '"Build Month", or a key ending in "buildMonth"' },
    { role: 'modelYear', label: 'Model year', accepts: '"Model Year", or a key ending in "modelYear"' },
    { role: 'certYear', label: 'Cert year', accepts: '"Cert Year", or a key ending in "certYear"' },
  ],
};

export interface DecodeSupportRole {
  label: string;
  /** Name of the field the decode writes this detail into, or null when no field was recognised. */
  fieldName: string | null;
}

export interface DecodeSupport {
  kind: DecodeKind;
  /** Name of the grouping object property, or null for top-level fields. */
  groupName: string | null;
  active: boolean;
  /** Why decoding is off, when it is. */
  issue: string | null;
  roles: DecodeSupportRole[];
}

/** Whether field dispatch gives this object property the Road Vehicle form's VIN decode. */
export function objectHasVinField(property: Property.Model<Property.PropertyType>): boolean {
  return hasIdField(VIN_DECODE, property);
}

/** Whether field dispatch gives this object property the Marine Vessel form's HIN decode. */
export function objectHasHinField(property: Property.Model<Property.PropertyType>): boolean {
  return hasIdField(HIN_DECODE, property);
}

/** Top-level fields the VIN decode fills in; empty unless a VIN sits alongside other vehicle details. */
export function resolveUngroupedVinKeys(assignments: PropertySet.PropertyAssignment[]): VehicleFieldKeys {
  return toActiveKeys(VIN_DECODE, matchRoles(VIN_DECODE, topLevelFields(assignments)));
}

/** Top-level fields the HIN decode fills in; empty unless a HIN sits alongside other vessel details. */
export function resolveUngroupedHinKeys(assignments: PropertySet.PropertyAssignment[]): MarineVesselFieldKeys {
  return toActiveKeys(HIN_DECODE, matchRoles(HIN_DECODE, topLevelFields(assignments)));
}

/** What VIN/HIN decoding an asset type's fields give the asset form, for showing to admins. */
export function getDecodeSupport(assignments: PropertySet.PropertyAssignment[]): DecodeSupport[] {
  const support: DecodeSupport[] = [];

  for (const assignment of assignments) {
    const property = assignment.property;
    if (!property || property.type !== Property.PropertyType.OBJECT) continue;

    const definition = groupedDefinition(property);
    if (!definition) continue;

    const matched = matchRoles(definition, objectFields(property as Property.ObjectModel));
    const active = !!matched[definition.idRole];
    support.push({
      kind: definition.kind,
      groupName: property.name,
      active,
      issue: active ? null : `No ${definition.kind} field was recognised in this group.`,
      roles: describeRoles(definition, matched),
    });
  }

  const fields = topLevelFields(assignments);
  for (const definition of [VIN_DECODE, HIN_DECODE] as DecodeDefinition<string>[]) {
    const matched = matchRoles(definition, fields);
    // Detail fields alone (e.g. a "Model" on a non-vehicle asset type) don't mean the type wants decoding.
    if (!matched[definition.idRole]) continue;

    const active = Object.keys(matched).length > 1;
    const details = definition.roles
      .filter(role => role.role !== definition.idRole)
      .map(role => role.label)
      .join(', ');
    support.push({
      kind: definition.kind,
      groupName: null,
      active,
      issue: active ? null : `Add at least one of ${details} for the ${definition.kind} to fill anything.`,
      roles: describeRoles(definition, matched),
    });
  }

  return support;
}

function groupedDefinition(property: Property.Model<Property.PropertyType>): DecodeDefinition<string> | null {
  // Mirrors field dispatch: an explicit form wins, then a VIN child, then a HIN child.
  if (property.formId === ROAD_VEHICLE_FORM_ID) return VIN_DECODE;
  if (property.formId === MARINE_VESSEL_FORM_ID) return HIN_DECODE;
  if (property.formId) return null;
  if (objectHasVinField(property)) return VIN_DECODE;
  if (objectHasHinField(property)) return HIN_DECODE;

  return null;
}

function hasIdField<R extends string>(
  definition: DecodeDefinition<R>,
  property: Property.Model<Property.PropertyType>
): boolean {
  if (property.type !== Property.PropertyType.OBJECT) return false;

  return objectFields(property as Property.ObjectModel).some(
    field => definition.roleOf(field.labels) === definition.idRole
  );
}

function objectFields(property: Property.ObjectModel): DecodeField[] {
  return (property.properties ?? [])
    .filter(assignment => assignment.property)
    .sort((a, b) => a.order - b.order)
    .map(assignment => ({
      labels: [assignment.displayLabel, assignment.property?.name, assignment.property?.valueKey],
      valueKey: assignment.property?.valueKey as string,
      name: assignment.displayLabel || (assignment.property?.name as string),
    }));
}

// Top-level assignments have no display label, so only the property's own name and key count.
function topLevelFields(assignments: PropertySet.PropertyAssignment[]): DecodeField[] {
  return assignments
    .filter(assignment => assignment.property && assignment.property.type !== Property.PropertyType.OBJECT)
    .map(assignment => ({
      labels: [assignment.property?.name, assignment.property?.valueKey],
      valueKey: assignment.property?.valueKey as string,
      name: assignment.property?.name as string,
    }));
}

/** The first field holding each role. */
function matchRoles<R extends string>(
  definition: DecodeDefinition<R>,
  fields: DecodeField[]
): Partial<Record<R, DecodeField>> {
  return fields.reduce<Partial<Record<R, DecodeField>>>((matched, field) => {
    const role = definition.roleOf(field.labels);
    if (role && !matched[role]) matched[role] = field;

    return matched;
  }, {});
}

function toActiveKeys<R extends string>(
  definition: DecodeDefinition<R>,
  matched: Partial<Record<R, DecodeField>>
): Partial<Record<R, string>> {
  const roles = Object.keys(matched) as R[];
  if (!matched[definition.idRole] || roles.length < 2) return {};

  return Object.fromEntries(roles.map(role => [role, matched[role]?.valueKey])) as Partial<Record<R, string>>;
}

function describeRoles<R extends string>(
  definition: DecodeDefinition<R>,
  matched: Partial<Record<R, DecodeField>>
): DecodeSupportRole[] {
  return definition.roles.map(role => ({ label: role.label, fieldName: matched[role.role]?.name ?? null }));
}
